import { noCacheUrl } from 'components/VisualStudioPlayground/Shapes/ImageShape/helpers'
import { getVideoSizeFromSrc } from './getVideoSizeFromSrc'
import { AssetRecordType, Editor, TLVideoAsset, createShapeId } from '@tldraw/tldraw'
import { getCurrentFrame, getVideo } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { IMediaFile } from './Types'
import { fitAndPosition } from 'object-fit-math'
import { VideoShape } from 'components/VisualStudioPlayground/Shapes/VideoShape/Types'
import { VideoShapeUtil } from 'components/VisualStudioPlayground/Shapes/VideoShape/VideoShapeUtil'
import { FitMode } from 'object-fit-math/dist/types'

/**
 * Create the asset for a video media file, without any transformations.
 *
 * @param file The media file to create an asset for
 */
export const createOriginalAssetFromFile = async (file: IMediaFile) => {
  let dimensions = file.width && file.height ? { width: file.width, height: file.height } : undefined
  if (!dimensions) {
    const videoSize = await getVideoSizeFromSrc(noCacheUrl(file.source_url))
    dimensions = { width: videoSize.w, height: videoSize.h }
  }
  return AssetRecordType.create({
    type: 'video',
    typeName: 'asset',
    meta: {
      mediaFileId: file.id,
      transformations: {},
    },
    props: {
      name: file.source_url,
      src: file.source_url,
      w: dimensions.width,
      h: dimensions.height,
      mimeType: file.mime_type,
      isAnimated: true,
    },
  }) as TLVideoAsset
}

/**
 * Create a video shape from a media file.
 *
 * @param editor The editor instance
 * @param file The media file to insert
 * @param options Options for inserting the image
 * @param options.w The width of the image
 * @param options.h The height of the image
 */
export const insertVideo = async (
  editor: Editor,
  file: IMediaFile,
  options: { w?: number; h?: number; replaceFit?: FitMode } = {},
) => {
  const { w, h, replaceFit } = options

  editor.batch(async () => {
    const asset = await createOriginalAssetFromFile(file)
    editor.createAssets([asset])

    const frame = getCurrentFrame(editor)
    const existingVideo = getVideo(editor, frame.id)

    // We currently only support one video per frame
    if (existingVideo) {
      const util = editor.getShapeUtil<VideoShape>(existingVideo) as VideoShapeUtil
      util.replace(
        existingVideo,
        asset.id,
        { duration: file.duration || 0, thumbnail_url: file.thumbnail_url || null },
        replaceFit,
      )
      return
    }

    const fittedVideo = fitAndPosition(
      { width: frame.props.w, height: frame.props.h },
      { width: asset.props.w, height: asset.props.h },
      'contain',
    )
    editor.createShape({
      id: createShapeId(),
      type: 'video',
      parentId: frame.id,
      x: frame.props.w / 2 - fittedVideo.width / 2,
      y: frame.props.h / 2 - fittedVideo.height / 2,
      props: {
        assetId: asset.id,
        w: w || fittedVideo.width,
        h: h || fittedVideo.height,
        thumbnail_url: file.thumbnail_url || null,
        duration: file.duration || 0,
      },
    })
  })
}
